import React from "react";
import { IntlShape, useIntl } from "@/i18n/i18n-client";
import {
  MenuItemType,
  StaticPageEnum,
  StoreMenuItemFragment,
} from "../generated/graphql";
import { useStore } from "@/lib/storeData/StoreContext";
import type { MenuWithChildrenType } from "../templates/default/components/NavBar/menu/types";
import type { NavBarProps } from "../templates/types";
import type { RendererComponent } from "./types";
import { useCartData } from "../lib/cartData/useCartData";

interface NavBarRendererProps extends RendererComponent<NavBarProps> {}

const NavBarRenderer: React.FC<NavBarRendererProps> = ({ Component }) => {
  const { appearance, socialMedia } = useStore();
  const intl = useIntl();
  const {
    cart: { items, receipt },
    setSideCartState,
  } = useCartData();

  const menus = appearance?.menus?.[0]?.items || fallbackMenu(intl);
  const menu = createHierarchicalTree(menus);
  const itemsCount = items?.reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

  return (
    <Component
      menu={menu}
      socialMedia={socialMedia}
      itemsTotal={receipt?.total!}
      itemsCount={itemsCount}
      toggleSideCart={() => setSideCartState(true)}
    />
  );
};

export default NavBarRenderer;

/**
 *
 * Helpers
 *
 */
const createHierarchicalTree = (
  items: (StoreMenuItemFragment | null)[],
  id: string | null = null
) => {
  return items
    .filter((item) => item?.parentId === id)
    .map((item) => ({
      ...item,
      children: createHierarchicalTree(items, item?.id),
    }));
};

const fallbackMenu = (intl: IntlShape): MenuWithChildrenType => [
  {
    id: "U3RvcmVNZW51SXRlbTpja25oZHY5OG0wMDAxZjh1bmc3N3Rjc2Yy",
    parentId: null,
    title: intl.formatMessage({ defaultMessage: "Home", id: "ejEGdx" }),
    type: MenuItemType["StaticPage"],
    locale: "ar",
    locales: ["ar"],
    handle: null,
    link: {
      __typename: "MenuLink",
      type: MenuItemType["StaticPage"],
      url: "",
      resourceId: null,
      openInNewTab: false,
      staticPageType: StaticPageEnum["Home"],
    },
    children: [],
  },
  {
    id: "U3RvcmVNZW51SXRlbTpja25oZHY5OG4wMDAyZjh1bmR6NWVhenVz",
    parentId: null,
    title: intl.formatMessage({ defaultMessage: "Shop", id: "Dxd1uB" }),
    type: MenuItemType["StaticPage"],
    locale: "ar",
    locales: ["ar"],
    handle: null,
    link: {
      __typename: "MenuLink",
      type: MenuItemType["StaticPage"],
      url: "",
      resourceId: null,
      openInNewTab: false,
      staticPageType: StaticPageEnum["Shop"],
    },
    children: [],
  },
  {
    __typename: "StoreMenuItem",
    id: "U3RvcmVNZW51SXRlbTpja25oZHY5OG4wMDAzZjh1bmhoeTZmdmlo",
    parentId: null,
    title: intl.formatMessage({ defaultMessage: "Contact", id: "zFegDD" }),
    type: MenuItemType["StaticPage"],
    locale: "ar",
    locales: ["ar"],
    handle: null,
    link: {
      __typename: "MenuLink",
      type: MenuItemType["StaticPage"],
      url: "",
      resourceId: null,
      openInNewTab: false,
      staticPageType: StaticPageEnum["Contact"],
    },
    children: [],
  },
];
